import { useEffect, useId, useRef, useState } from "react";

import { DisclosureLink, Money, Status } from "../../../../components/ui";
import { payslipEarnings } from "../payslipEarnings";
import { BasicPayWorking, CalculationCheck, EarningNotes, NetPayEquation } from "./PayCalculation";
import { NOT_STORED_AT_ISSUE, attendanceFacts, dateTime, deductionRows, employerPaid, issuedIdentity, minutes, payDate, paymentOf, periodDates, periodLabel, suspensionNote, workedOut } from "./payslipView";
import "./payslip-print.css";

// The payslip as one document, the same on screen and on paper: who and when, net pay, earnings, what was taken
// from the employee's pay, what the employer paid on top, and — apart from the calculation — whether the salary has
// been paid. Every figure is the one payroll stored. No government number, internal id, code or reviewer appears.

const section = "border-t border-subtle px-4 py-5 sm:px-6";
const heading = "text-section-title text-foreground";

function Fact({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-meta text-secondary">{label}</dt>
      <dd className="mt-0.5 break-words text-body-sm font-medium text-foreground">{children}</dd>
    </div>
  );
}

function Row({ label, note, cents, text }) {
  return (
    <tr className="border-b border-subtle">
      <th scope="row" className="whitespace-normal py-2 pr-4 text-left align-top font-normal text-foreground">
        {label}
        {note ? <span className="mt-0.5 block text-meta text-secondary">{note}</span> : null}
      </th>
      <td className="whitespace-nowrap py-2 text-right align-top text-foreground">
        {text ?? <Money value={cents} />}
      </td>
    </tr>
  );
}

function Total({ label, cents, closing = false }) {
  return (
    <tr className={closing ? "border-b-[3px] border-double border-strong" : "border-b border-default last:border-b-0"}>
      <th scope="row" className={`whitespace-normal py-2.5 pr-4 text-left text-foreground ${closing ? "text-body font-semibold" : "font-semibold"}`}>{label}</th>
      <td className={`whitespace-nowrap py-2.5 text-right text-foreground ${closing ? "text-money font-semibold" : "font-semibold"}`}>
        <Money value={cents} />
      </td>
    </tr>
  );
}

/** A ledger table: a labelled amount per row, totals in the foot. `data-ui` opts out of the legacy table restyling. */
function Ledger({ caption, column, children, foot }) {
  return (
    <table data-ui="payslip-table" className="mt-2 w-full border-collapse border-t border-default text-body-sm">
      <caption className="sr-only">{caption}</caption>
      <thead className="sr-only">
        <tr>
          <th scope="col">{column}</th>
          <th scope="col">Amount</th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
      {foot ? <tfoot>{foot}</tfoot> : null}
    </table>
  );
}

/**
 * `payslipState` and `payment` are for a reader who is not the employee (HR opening the stored payslip): the state of
 * the payslip and of its salary in their words. The figures are the stored ones for every reader.
 *
 * APP-HR-103: the employer, employee, position and department are the ones stored when the payslip was released.
 * `businessName` and `employee` are today's names; they are used only for a payslip that has no stored details, and
 * are then marked as today's.
 */
export default function PayslipDocument({ payslip, businessName, employee, payslipState = "Released", payment: paymentOverride = null }) {
  const ids = useId();
  const root = useRef(null);
  const [printedAt, setPrintedAt] = useState(() => new Date());

  // Paper has no disclosures: open them for the print and put them back afterwards.
  useEffect(() => {
    const opened = [];
    const before = () => {
      setPrintedAt(new Date());
      root.current?.querySelectorAll("details:not([open])").forEach((details) => {
        details.open = true;
        opened.push(details);
      });
    };
    const after = () => {
      while (opened.length) opened.pop().open = false;
    };
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, []);

  const earnings = payslipEarnings(payslip);
  // Report 54A (D1): the stored working of this pay, built by the server from the payslip's own lines.
  const calculation = payslip.calculation || null;
  // The steps behind basic pay are shown in their own section; the earnings row then points to it.
  const worked = Boolean(calculation?.basic?.stored);
  // What the stored breakdown says about a deduction line (a loan installment), by the payslip's own deduction id.
  const storedNote = (key) => calculation?.deductions?.lines?.find((line) => line.code === String(key).replace(/^other-/, "deduction-"))?.note || undefined;
  const deductions = deductionRows(payslip);
  const employer = employerPaid(payslip);
  const explained = workedOut(payslip);
  const attendance = attendanceFacts(payslip);
  const payment = paymentOverride || paymentOf(payslip);
  const suspension = suspensionNote(payslip);
  const period = periodLabel(payslip);
  const dates = periodDates(payslip);
  const identity = issuedIdentity(payslip, { businessName, employee });
  const employeeLine = [identity.employee, identity.employeeCode].filter(Boolean).join(" · ");

  return (
    <article ref={root} aria-label={`Payslip for ${period}`} className="payslip-doc rounded-panel border border-default bg-surface text-foreground">
      <header className="px-4 py-5 sm:px-6">
        <div className="mb-3 hidden print:block">
          {identity.employer ? <p className="text-page-title text-foreground">{identity.employer}</p> : null}
          <p className="text-section-title text-foreground">Payslip</p>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 print:grid-cols-5 print:gap-x-4">
          {identity.employer ? <Fact label="Employer">{identity.employer}</Fact> : null}
          {employeeLine ? <Fact label="Employee">{employeeLine}</Fact> : null}
          {identity.position ? <Fact label="Position">{identity.position}</Fact> : null}
          {identity.department ? <Fact label="Department">{identity.department}</Fact> : null}
          <Fact label="Pay period">{period}{dates && dates !== period ? <span className="block font-normal text-secondary">{dates}</span> : null}</Fact>
          {payDate(payslip) ? <Fact label="Pay date">{payDate(payslip)}</Fact> : null}
          {payslip.payslip_number ? <Fact label="Payslip no."><span className="tabular-nums">{payslip.payslip_number}</span></Fact> : null}
          {identity.issuedAt ? <Fact label="Issued">{dateTime(identity.issuedAt)}</Fact> : null}
        </dl>
        {identity.legacy && (identity.employer || employeeLine) ? (
          <p className="mt-3 text-meta text-secondary">
            Employer and employee names are shown {NOT_STORED_AT_ISSUE}. The amounts are the ones stored with the payslip.
          </p>
        ) : null}
      </header>

      <section aria-labelledby={`${ids}-net`} className={section}>
        <h2 id={`${ids}-net`} className="text-label text-secondary">Net pay</h2>
        <p className="mt-1 text-metric text-foreground"><Money value={payslip.net_pay_cents} /></p>
        {calculation ? <NetPayEquation calculation={calculation} className="mt-1" /> : (
          <p className="mt-1 text-body-sm text-secondary">
            <Money value={earnings.grossCents} className="text-foreground" /> gross pay, less{" "}
            <Money value={payslip.total_deductions_cents} className="text-foreground" /> deducted from your pay.
          </p>
        )}
        <CalculationCheck calculation={calculation} />
      </section>

      {calculation ? (
        <section aria-labelledby={`${ids}-basic`} className={`${section} payslip-working`}>
          <h2 id={`${ids}-basic`} className={heading}>How your basic pay was worked out</h2>
          {calculation.basic.payBasisLabel ? <p className="mt-0.5 text-body-sm text-secondary">{calculation.basic.payBasisLabel}. Unpaid days are taken out here, once; they are not deducted again below.</p> : null}
          <BasicPayWorking calculation={calculation} caption="How your basic pay was worked out" />
        </section>
      ) : null}

      <div className="print:grid print:grid-cols-2 print:gap-x-8">
        <section aria-labelledby={`${ids}-earnings`} className={section}>
          <h2 id={`${ids}-earnings`} className={heading}>Earnings</h2>
          <Ledger caption="Earnings" column="Earning" foot={<Total label="Gross pay" cents={earnings.grossCents} />}>
            {earnings.lines.map((line) => (
              <Row
                key={line.key}
                label={line.key === "base" && worked ? "Basic pay" : line.label}
                cents={line.cents}
                note={
                  line.key === "base" && worked ? "Worked out above"
                    : line.key === "base" && line.absenceCents > 0 ? <>After <Money value={line.absenceCents} /> for {suspension ? "suspended days, absences" : "absences"} and unpaid leave</>
                    : line.key === "overtime" && line.minutes > 0 ? `${minutes(line.minutes)} of approved overtime`
                      : line.note
                }
              />
            ))}
          </Ledger>
          <EarningNotes calculation={calculation} />
        </section>

        <section aria-labelledby={`${ids}-deductions`} className={section}>
          <h2 id={`${ids}-deductions`} className={heading}>Deducted from your pay</h2>
          <Ledger
            caption="Deducted from your pay"
            column="Deduction"
            foot={
              <>
                <Total label="Total deductions" cents={payslip.total_deductions_cents} />
                <Total closing label="Net pay" cents={payslip.net_pay_cents} />
              </>
            }
          >
            {deductions.length ? deductions.map((row) => <Row key={row.key} label={row.label} note={row.note || storedNote(row.key)} cents={row.cents} text={row.text} />) : (
              <tr className="border-b border-subtle">
                <td colSpan={2} className="py-2 text-secondary">Nothing was deducted on this payslip.</td>
              </tr>
            )}
          </Ledger>
        </section>
      </div>

      <div className={employer.rows.length && explained.items.length ? "print:grid print:grid-cols-2 print:gap-x-8" : undefined}>
        {employer.rows.length ? (
          <section aria-labelledby={`${ids}-employer`} className={section}>
            <h2 id={`${ids}-employer`} className={heading}>Paid by your employer</h2>
            <p className="mt-0.5 text-body-sm text-secondary">Your employer pays these on top of your pay. They are not deducted from it.</p>
            <Ledger caption="Paid by your employer, not deducted from your pay" column="Contribution" foot={<Total label="Total paid by your employer" cents={employer.totalCents} />}>
              {employer.rows.map((row) => <Row key={row.key} label={row.label} cents={row.cents} />)}
            </Ledger>
          </section>
        ) : null}

        {explained.items.length ? (
          <div className="payslip-explained border-t border-subtle px-4 py-2 sm:px-6">
            <DisclosureLink label="How these amounts were worked out" contentClassName="space-y-3 pb-3 text-body-sm text-secondary">
              {explained.monthLabel ? (
                <p>Your SSS, PhilHealth and Pag-IBIG for {explained.monthLabel} are taken once, on the last payslip of the month.</p>
              ) : null}
              <dl className="space-y-2">
                {explained.items.map((item) => (
                  <div key={item.key}>
                    <dt className="font-medium text-foreground">{item.label}</dt>
                    <dd>
                      {item.text}
                      {item.rule ? <span className="block text-meta">Rule used: {item.rule}</span> : null}
                      {item.rounding ? <span className="block text-meta">{item.rounding}</span> : null}
                    </dd>
                  </div>
                ))}
              </dl>
              {explained.sources.length ? <p className="text-meta">Pay counted for the month: {explained.sources.join(", ")}.</p> : null}
            </DisclosureLink>
          </div>
        ) : null}
      </div>

      {attendance.length || suspension ? (
        <section aria-labelledby={`${ids}-attendance`} className={section}>
          <h2 id={`${ids}-attendance`} className={heading}>Attendance counted</h2>
          {attendance.length ? (
            <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
              {attendance.map((fact) => <Fact key={fact.label} label={fact.label}>{fact.value}</Fact>)}
            </dl>
          ) : null}
          {suspension ? <p className="mt-2 text-body-sm text-secondary">{suspension}. Suspended days are not counted as absences.</p> : null}
        </section>
      ) : null}

      <section aria-labelledby={`${ids}-payment`} className={section}>
        <h2 id={`${ids}-payment`} className={heading}>Payment</h2>
        <dl className="mt-2 grid grid-cols-1 gap-y-3 sm:grid-cols-3 sm:gap-x-6">
          <Fact label="Payslip">{payslipState}</Fact>
          <div className="min-w-0 sm:col-span-2">
            <dt className="text-meta text-secondary">Salary payment</dt>
            <dd className="mt-0.5 text-body-sm">
              <Status tone={payment.tone} icon={payment.tone !== "neutral"} className="font-medium">
                {payment.state || payment.label}{payment.date ? ` · ${payment.date}` : ""}
              </Status>
              <span className="mt-0.5 block text-secondary">{payment.detail}</span>
            </dd>
          </div>
        </dl>
      </section>

      <footer className="hidden border-t border-subtle px-4 py-3 text-meta text-secondary print:block sm:px-6">
        Printed {dateTime(printedAt)}. This payslip shows how your pay was calculated. It is not a receipt of payment.
      </footer>
    </article>
  );
}
